import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Clock, AlertTriangle } from 'lucide-react';
import { AdminUser } from '../../services/userManagementService';

interface SuspendAccountModalProps {
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userId: string, reason: string, suspendUntil?: Date) => void;
  isLoading: boolean;
}

const SuspendAccountModal: React.FC<SuspendAccountModalProps> = ({
  user,
  isOpen,
  onClose,
  onConfirm,
  isLoading
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState<string>('1h');

  const handleConfirm = () => {
    if (!user || !reason.trim()) return;

    let suspendUntil: Date | undefined;
    
    if (duration !== 'indefinite') {
      suspendUntil = new Date();
      switch (duration) {
        case '1h':
          suspendUntil.setHours(suspendUntil.getHours() + 1);
          break;
        case '24h':
          suspendUntil.setHours(suspendUntil.getHours() + 24);
          break;
        case '7d':
          suspendUntil.setDate(suspendUntil.getDate() + 7);
          break;
        case '30d':
          suspendUntil.setDate(suspendUntil.getDate() + 30);
          break;
      }
    }

    onConfirm(user.id, reason, suspendUntil);
    setReason('');
    setDuration('1h');
  };

  const handleClose = () => {
    setReason('');
    setDuration('1h');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-orange-600" />
            {t('adminPanel.suspendAccount.title')}
          </DialogTitle>
          <DialogDescription>
            {t('adminPanel.suspendAccount.description')} <strong>{user?.full_name}</strong> ({user?.email})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-orange-50 border border-orange-200 rounded-md p-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5" />
            <p className="text-sm text-orange-800">
              {t('adminPanel.suspendAccount.warning')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="duration">{t('adminPanel.suspendAccount.duration')}</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger id="duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">{t('adminPanel.suspendAccount.durations.1h')}</SelectItem>
                <SelectItem value="24h">{t('adminPanel.suspendAccount.durations.24h')}</SelectItem>
                <SelectItem value="7d">{t('adminPanel.suspendAccount.durations.7d')}</SelectItem>
                <SelectItem value="30d">{t('adminPanel.suspendAccount.durations.30d')}</SelectItem>
                <SelectItem value="indefinite">{t('adminPanel.suspendAccount.durations.indefinite')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">{t('adminPanel.suspendAccount.reasonLabel')}</Label>
            <Textarea
              id="reason"
              placeholder={t('adminPanel.suspendAccount.reasonPlaceholder')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!reason.trim() || isLoading}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {isLoading ? t('adminPanel.suspendAccount.suspending') : t('adminPanel.suspendAccount.suspendButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SuspendAccountModal;
